import { useEffect, useMemo, useState } from "react";
import { BsCheckCircleFill, BsXCircleFill } from "react-icons/bs";
import { FiCheckSquare, FiFileText, FiLayers, FiList } from "react-icons/fi";
import { LuPencil } from "react-icons/lu";
import { RiDeleteBinLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import FilterExternalDropdown from "../../components/FilterExternalDropdown";
import NewTableComponent from "../../components/NewTableComponent";
import Stats from "../../components/Stats";
import UserAvatar from "../../components/UserAvatar";
import ConfirmDeleteModal from "../../components/modals/ConfirmDeleteModal";
import { apiService } from "../../services/apiService";
import {
  fetchCustomerDropdown,
  fetchUsersDropdown,
} from "../../store/Slices/dropdownSlice";
import "../../styles/Access.css";
import "../../styles/Services.css";
import "../../styles/Table.css";
import "../../styles/Trip.css";
import "../../styles/TripCostEstimation.css";
import {
  AppMainModules,
  ContractPermission,
} from "../../utils/app-constant";
import { hasPermission } from "../../utils/helper";
import ExportButton from "../../components/ExportButton";
import { plusIcon } from "../../utils/svgs";
import { errorNotify, successNotify } from "../../utils/toast";
import { useContractsListingExport } from "./useContractsListingExport";

const formatNumber = (value) =>
  new Intl.NumberFormat("en-PK", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const buildStatIcon = (icon) => <span className="finance_metric_icon">{icon}</span>;

const safeArray = (value) => (Array.isArray(value) ? value : []);

const toText = (value) => String(value ?? "").trim();

const formatDateOnly = (value) => {
  const rawValue = toText(value);
  if (!rawValue) return "-";

  const directDateMatch = rawValue.match(/^\d{4}-\d{2}-\d{2}/);
  if (directDateMatch) {
    return directDateMatch[0];
  }

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) return rawValue;

  return parsedDate.toISOString().slice(0, 10);
};

const formatStatusLabel = (value) => {
  const normalizedValue = toText(value).toLowerCase();
  if (!normalizedValue) return "-";
  return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
};

const normalizeDropdownOptions = (options = []) =>
  safeArray(options)
    .map((option) => {
      const value = option?.value ?? option?.id ?? "";
      const label = toText(option?.label || option?.name || option?.title || value);

      if (value === "" || !label) return null;

      return {
        value,
        label,
        image: toText(option?.image),
      };
    })
    .filter(Boolean);

const buildOptionLookups = (options = []) => {
  const normalizedOptions = normalizeDropdownOptions(options);

  return {
    byValue: new Map(
      normalizedOptions.map((option) => [String(option.value), option])
    ),
    byLabel: new Map(
      normalizedOptions.map((option) => [option.label.toLowerCase(), option])
    ),
  };
};

const CONTRACT_STATUS_FILTER_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const buildPricingTemplateApiFilters = (searchFilters = {}) =>
  Object.entries(searchFilters || {}).reduce((filters, [filterName, filterMeta]) => {
    const rawValue = filterMeta?.value;
    const filterType = filterMeta?.filter_type;

    if (
      !filterType ||
      rawValue === undefined ||
      rawValue === null ||
      rawValue === "" ||
      (Array.isArray(rawValue) && rawValue.length === 0)
    ) {
      return filters;
    }

    filters[filterName] = filterMeta;

    return filters;
  }, {});

const normalizePricingTemplateApiRow = (
  item = {},
  index = 0,
  customerLookup = { byValue: new Map() },
  userLookup = { byValue: new Map() }
) => {
  const templateId =
    toText(item?.contracts_template_id) || `CTT-${String(item?.id || index + 1)}`;
  const description = toText(item?.description);
  const descriptionParts = description
    .split("|")
    .map((part) => toText(part))
    .filter(Boolean);
  const customerId = item?.customer_id ?? item?.customer?.id ?? "";
  const customerOption = customerLookup.byValue.get(String(customerId));
  const creatorId = item?.create_by ?? item?.creator?.id ?? "";
  const creatorOption = userLookup.byValue.get(String(creatorId));
  const creatorName = toText(
    creatorOption?.label ||
    item?.creator?.username ||
    item?.creator?.name ||
    (creatorId !== "" ? `User #${creatorId}` : "")
  );
  const normalizedStatus = toText(item?.status).toLowerCase();

  return {
    id: item?.id ?? `${templateId}-${index}`,
    templateId,
    name: toText(item?.name) || descriptionParts[0] || templateId,
    customer: toText(
      customerOption?.label ||
      item?.customer?.name ||
      (customerId !== "" ? `Customer #${customerId}` : "")
    ) || "-",
    status: formatStatusLabel(normalizedStatus),
    statusValue: normalizedStatus,
    effectiveFrom: formatDateOnly(item?.start_at),
    effectiveTo: formatDateOnly(item?.expire_at),
    createdBy: creatorName || "-",
    createdByImage: toText(creatorOption?.image || item?.creator?.image),
    createdDate: formatDateOnly(item?.created_at || item?.updated_at),
    description,
    components: safeArray(item?.contracts_vehicle_types ?? item?.contract_template_vehicle_types).length,
  };
};

const getPricingTemplatesOverview = (rows = []) => {
  const normalizedRows = safeArray(rows);

  return {
    totalTemplates: normalizedRows.length,
    activeTemplates: normalizedRows.filter((row) => row?.statusValue === "active").length,
    customersAssigned: new Set(normalizedRows.map((row) => row?.customer).filter(Boolean)).size,
    totalComponents: normalizedRows.reduce(
      (total, row) => total + Number(row?.components || 0),
      0
    ),
  };
};

const Contracts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const canCreateTemplate = hasPermission({
    permissionSlug: ContractPermission.Create,
    menuSlug: "contract",
    moduleSlug: AppMainModules.contract,
  });
  const canUpdateTemplate = hasPermission({
    permissionSlug: ContractPermission.Update,
    menuSlug: "contract",
    moduleSlug: AppMainModules.contract,
  });
  const canDeleteTemplate = hasPermission({
    permissionSlug: ContractPermission.Delete,
    menuSlug: "contract",
    moduleSlug: AppMainModules.contract,
  });
  const { customerDropdownData = [], usersDropdownData = [] } = useSelector(
    (state) => state.dropdowns || {}
  );

  const [apiRows, setApiRows] = useState([]);
  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(8);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [locallyHiddenIds, setLocallyHiddenIds] = useState([]);

  const customerLookup = useMemo(
    () => buildOptionLookups(customerDropdownData),
    [customerDropdownData]
  );
  const userLookup = useMemo(
    () => buildOptionLookups(usersDropdownData),
    [usersDropdownData]
  );
  const customerFilterOptions = useMemo(
    () => normalizeDropdownOptions(customerDropdownData),
    [customerDropdownData]
  );
  const userFilterOptions = useMemo(
    () => normalizeDropdownOptions(usersDropdownData),
    [usersDropdownData]
  );
  const apiSearchFilters = useMemo(
    () => buildPricingTemplateApiFilters(searchFilters),
    [searchFilters]
  );

  const {
    hasActiveFilters,
    exportLoading,
    pdfExportLoading,
    excelExportLoading,
    handleExportPdf,
    handleExportExcel,
  } = useContractsListingExport(searchText, apiSearchFilters);

  useEffect(() => {
    dispatch(fetchCustomerDropdown());
    dispatch(fetchUsersDropdown());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [limit, searchFilters, searchText]);

  useEffect(() => {
    let isMounted = true;

    const fetchPricingTemplates = async () => {
      setLoading(true);

      try {
        const response = await apiService.getContractsPaginated({
          page: currentPage,
          limit,
          search_text: searchText,
          searchFilters: apiSearchFilters,
        });

        const payload = response?.data || {};
        const nextRows = safeArray(payload?.results);

        if (!isMounted) return;

        setApiRows(nextRows);
        setMeta({
          totalItems: Number(payload?.meta?.totalItems || payload?.total || nextRows.length),
          totalPages: Number(payload?.meta?.totalPages || 1),
        });
      } catch (error) {
        if (!isMounted) return;
        setApiRows([]);
        setMeta({ totalItems: 0, totalPages: 1 });
        errorNotify(error || "Failed to fetch contracts");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPricingTemplates();

    return () => {
      isMounted = false;
    };
  }, [apiSearchFilters, currentPage, limit, searchText]);

  const rows = useMemo(
    () =>
      apiRows
        .map((row, index) =>
          normalizePricingTemplateApiRow(row, index, customerLookup, userLookup)
        )
        .filter((row) => !locallyHiddenIds.includes(String(row?.id ?? ""))),
    [apiRows, customerLookup, locallyHiddenIds, userLookup]
  );

  const totalItems = Math.max(
    0,
    Number(meta?.totalItems || rows.length) - locallyHiddenIds.length
  );
  const totalPages = Math.max(1, Number(meta?.totalPages || 1));

  const selectedDeleteRow = useMemo(
    () => rows.find((row) => String(row.id) === String(deleteId || "")) || null,
    [deleteId, rows]
  );
  const overview = useMemo(() => getPricingTemplatesOverview(rows), [rows]);

  const statsData = useMemo(
    () => [
      {
        title: "Total Contracts",
        value: formatNumber(totalItems),
        text: <span>Total contracts configured</span>,
        icon: buildStatIcon(<FiFileText />),
      },
      {
        title: "Active Contracts",
        value: formatNumber(overview.activeTemplates),
        text: <span>Active contracts in current results</span>,
        className: "completed",
        icon: buildStatIcon(<FiLayers />),
      },
      {
        title: "Customers Assigned",
        value: formatNumber(overview.customersAssigned),
        text: <span>Customers visible in current results</span>,
        className: "late",
        icon: buildStatIcon(<FiCheckSquare />),
      },
      {
        title: "Total Components",
        value: formatNumber(overview.totalComponents),
        text: <span>Components returned with current results</span>,
        className: "pending-delivery",
        icon: buildStatIcon(<FiList />),
      },
    ],
    [overview, totalItems]
  );

  const handleCreateClick = () => {
    navigate("/contract/create");
  };

  const handleOpenDetails = (row) => {
    if (!row?.id) return;
    navigate(`/contract/${row.id}`, {
      state: { template: row },
    });
  };

  const closeDeletePopup = () => {
    setDeletePopup(false);
    setDeleteId(null);
  };

  const handleDelete = async () => {
    if (deleteId === null || deleteId === undefined || deleteLoading) return;
    if (!canDeleteTemplate) {
      errorNotify("You do not have permission to delete contracts.");
      return;
    }

    setDeleteLoading(true);

    try {
      await apiService.deleteContract(deleteId);
      setLocallyHiddenIds((previous) =>
        previous.includes(String(deleteId))
          ? previous
          : [...previous, String(deleteId)]
      );
      closeDeletePopup();
      successNotify("Contract deleted successfully");
    } catch (error) {
      errorNotify(error || "Failed to delete contract");
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      name: "Contract ID",
      key: "templateId",
      selector: (row) => (
        <Tooltip title={row.templateId} placement="topLeft">
          <button
            type="button"
            className="finance_link_text finance_link_button truncate_cell_text"
            onClick={() => handleOpenDetails(row)}
          >
            {row.templateId}
          </button>
        </Tooltip>
      ),
      tableColumn: true,
      filterName: "templateIdFilter",
      type: "text",
      minWidth: "170px",
    },
    {
      name: "Name",
      key: "name",
      selector: (row) => (
        <Tooltip title={row.name} placement="topLeft">
          <span className="truncate_cell_text">{row.name}</span>
        </Tooltip>
      ),
      tableColumn: true,
      filterName: "nameFilter",
      type: "text",
      minWidth: "260px",
    },
    {
      name: "Customer",
      key: "customer",
      selector: (row) => (
        <Tooltip title={row.customer} placement="topLeft">
          <span className="truncate_cell_text">{row.customer}</span>
        </Tooltip>
      ),
      tableColumn: true,
      filterName: "customerIdFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: customerFilterOptions,
      minWidth: "190px",
    },
    {
      name: "Status",
      key: "status",
      selector: (row) => (
        <div
          className={`pricing_template_status ${row.statusValue === "active" ? "is_active" : "is_inactive"
            }`}
        >
          {row.statusValue === "active" ? <BsCheckCircleFill /> : <BsXCircleFill />}
          <span>{row.status}</span>
        </div>
      ),
      tableColumn: true,
      filterName: "statusFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: CONTRACT_STATUS_FILTER_OPTIONS,
      minWidth: "150px",
    },
    {
      name: "Effective From",
      key: "effectiveFrom",
      selector: (row) => row.effectiveFrom,
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "Effective To",
      key: "effectiveTo",
      selector: (row) => row.effectiveTo,
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "Created by",
      key: "createdBy",
      selector: (row) => (
        <Tooltip title={row.createdBy} placement="topLeft">
          <div className="finance_created_by_cell">
            <UserAvatar
              username={row.createdBy}
              image={row.createdByImage}
              marginRight="0px"
            />
            <span className="finance_created_by_name truncate_cell_text">{row.createdBy}</span>
          </div>
        </Tooltip>
      ),
      tableColumn: true,
      filterName: "createdByFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: userFilterOptions,
      minWidth: "220px",
    },
    {
      name: "Created date",
      key: "createdDate",
      selector: (row) => row.createdDate,
      tableColumn: false,
      minWidth: "150px",
    },
    {
      name: "Action",
      key: "action",
      selector: (row) => (
        <div className="table_action_btn">
          {canUpdateTemplate && (
            <button
              type="button"
              className="edit_btn"
              title="Edit contract"
              disabled={true}
              style={{ cursor: "not-allowed", opacity: 0.5 }}
            >
              <LuPencil />
            </button>
          )}
          {canDeleteTemplate && (
            <button
              type="button"
              className="delete_btn"
              title="Delete contract"
              onClick={() => {
                setDeleteId(row.id);
                setDeletePopup(true);
              }}
            >
              <RiDeleteBinLine />
            </button>
          )}
        </div>
      ),
      tableColumn: false,
      minWidth: "100px",
      grow: 0,
    },
  ];

  return (
    <div className="trip_cost_estimation_page pricing_templates_page">
      <Breadcrumbs values={["Contract"]} />

      <ConfirmDeleteModal
        show={deletePopup}
        onClose={closeDeletePopup}
        onConfirm={handleDelete}
        loading={deleteLoading}
        title="Are you sure you want to delete this contract?"
        description={`Deleting "${selectedDeleteRow?.templateId || "this contract"
          }" will permanently remove it.`}
      />

      <div className="access_view">
        <h2>Contract</h2>

        <div className="services_buttons">
          <ExportButton
            type="pdf"
            onClick={handleExportPdf}
            disabled={!hasActiveFilters || exportLoading}
            loading={pdfExportLoading}
            hasActiveFilters={hasActiveFilters}
          />

          <ExportButton
            type="excel"
            onClick={handleExportExcel}
            disabled={!hasActiveFilters || exportLoading}
            loading={excelExportLoading}
            hasActiveFilters={hasActiveFilters}
          />

          {canCreateTemplate && (
            <button type="button" onClick={handleCreateClick}>
              {plusIcon} Create Contract
            </button>
          )}
        </div>
      </div>

      <Stats statsData={statsData} numberOfColumn={3} />

      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={rows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={(nextLimit) => {
          setLimit(nextLimit);
          setCurrentPage(1);
        }}
        totalItems={totalItems}
        totalPages={totalPages}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={loading}
        paginationLoading={loading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        limitOptions={[8, 10, 20, 50]}
        tableClassName="pricing_templates_table parts_table_fix"
      />

      {!loading && totalItems === 0 && (
        <div className="add_trip_suggestion_div" style={{ marginTop: 0 }}>
          <h4>There is nothing here...</h4>
          <p>
            There is nothing to view right now, please add a contract to
            get started.
          </p>
          {canCreateTemplate && (
            <Button
              submitText={`${plusIcon} Create Contract`}
              type="button"
              shouldRow={false}
              isShowCancel={false}
              submitBtn={handleCreateClick}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Contracts;